import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { AuthLayout, Field } from "@/components/auth/AuthLayout";
import { api, formatApiError } from "@/api/client";

export default function ForgotPassword() {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setErr(null);
        try {
            await api.post("/auth/forgot-password", { email: email.trim() });
            setDone(true);
        } catch (e: any) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout title={t("auth.forgot.title")} backHref="/login" backLabelKey="auth.back_to_login">
            {!done ? (
                <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
                    <p className="text-sm" style={{ color: "var(--text-muted)" }}>{t("auth.forgot.desc")}</p>
                    <Field label={t("auth.email")}>
                        <input data-testid="forgot-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} />
                    </Field>
                    {err && <div className="text-sm" style={{ color: "var(--error-fg)" }}>{err}</div>}
                    <button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                        {t("auth.forgot.submit")}
                    </button>
                    <Link to="/login" className="text-sm hover:underline block text-center" style={{ color: "var(--accent)" }}>{t("auth.back_to_login")}</Link>
                </form>
            ) : (
                <div data-testid="forgot-done" className="space-y-4">
                    <div className="flex items-start gap-2 rounded-md p-4" style={{ background: "var(--info-bg)", color: "var(--info-fg)" }}>
                        <CheckCircle2 size={18} className="mt-0.5" />
                        <div className="text-sm">
                            <div className="font-medium">{t("auth.forgot.sent_title")}</div>
                            <p className="mt-1">{t("auth.forgot.sent_desc_mocked")}</p>
                        </div>
                    </div>
                    <Link to="/login" className="text-sm hover:underline block text-center" style={{ color: "var(--accent)" }}>{t("auth.back_to_login")}</Link>
                </div>
            )}
        </AuthLayout>
    );
}
